import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  Divider, 
  IconButton, 
  Menu, 
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  TextField as MuiTextField
} from '@mui/material';
import { 
  Save as SaveIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  MoreVert as MoreIcon,
  AutoAwesome as AIIcon,
  Warning as WarningIcon,
  Lightbulb as TipIcon,
  FormatQuote as QuoteIcon,
  Translate as TranslateIcon,
  Summarize as SummarizeIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  People as PeopleIcon,
  FileCopy as FileCopyIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { documentAPI, templateAPI, aiAPI } from '../services/api';
import EncryptionSettings from '../components/documents/EncryptionSettings';
import EncryptionBadge from '../components/documents/EncryptionBadge';
import { CollaborationProvider } from '../components/documents/CollaborationProvider';
import ActiveCollaborators from '../components/documents/ActiveCollaborators';

// Add this interface near the top of the file, after the imports
interface DocumentSection {
  id: number;
  title: string;
  content: string;
}

// Rich text editor (in a real app, you'd use a library like Draft.js, Quill, or TinyMCE)
// For this example, we'll use a simplified version with a textarea
const DocumentEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  const navigate = useNavigate();
  
  // Check if we're in template creation mode
  const isTemplateCreation = window.location.pathname === '/templates/new';
  const isTemplateEdit = window.location.pathname.startsWith('/templates/edit/');
  
  // Document state
  const [docData, setDocData] = useState<any>({
    title: '',
    content: '',
    sections: [],
    isEncrypted: true,
    metadata: {
      author: 'Current User',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // AI features state
  const [aiDrawerOpen, setAIDrawerOpen] = useState(false);
  const [aiFeature, setAIFeature] = useState<string | null>(null);
  const [aiLoading, setAILoading] = useState(false);
  const [aiResult, setAIResult] = useState<string | null>(null);
  const [riskAnalysis, setRiskAnalysis] = useState<any[]>([]);
  
  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Security settings
  const [showSecuritySettings, setShowSecuritySettings] = useState(false);
  
  // Collaboration dialog
  const [showCollaborationDialog, setShowCollaborationDialog] = useState(false);
  
  // New state for dialogs
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showVersionHistoryDialog, setShowVersionHistoryDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  
  // Mock sections for demonstration
  const mockSections = [
    { id: 1, title: 'Introduction', content: 'This Agreement is made and entered into as of [DATE], by and between the undersigned parties.' },
    { id: 2, title: 'Scope of Services', content: 'The Contractor shall provide the following services: [DESCRIPTION OF SERVICES].' },
    { id: 3, title: 'Compensation', content: 'The Client shall pay the Contractor a fee of [AMOUNT] for the services provided.' },
    { id: 4, title: 'Term and Termination', content: 'This Agreement shall commence on [START DATE] and continue until [END DATE], unless terminated earlier.' },
    { id: 5, title: 'Confidentiality', content: 'The Contractor agrees to maintain the confidentiality of all proprietary information provided by the Client.' },
  ];
  
  useEffect(() => {
    const loadDocument = async () => {
      setLoading(true);
      
      try {
        if (id && !isTemplateCreation) {
          // Fetch existing document
          const response = await documentAPI.getById(id);
          setDocData(response.data);
        } else if (templateId) {
          // Create new document from template
          const response = await templateAPI.getById(templateId);
          setDocData({
            title: `New ${response.data.name}`,
            content: response.data.content,
            sections: response.data.sections || mockSections,
            isEncrypted: true,
            metadata: {
              templateId: templateId,
              author: 'Current User',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }
          });
        } else {
          // New blank document or template
          setDocData({
            title: isTemplateCreation ? 'New Template' : 'Untitled Document',
            content: '',
            sections: mockSections,
            isEncrypted: isTemplateCreation ? false : true, // Templates don't need encryption
            metadata: {
              author: 'Current User',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isTemplate: isTemplateCreation
            }
          });
        }
      } catch (error) {
        console.error('Error loading document:', error);
        // Use mock data for demo
        setDocData({
          title: id ? 'Sample Contract' : (isTemplateCreation ? 'New Template' : 'Untitled Document'),
          content: '',
          sections: mockSections,
          isEncrypted: isTemplateCreation ? false : true,
          metadata: {
            author: 'Current User',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isTemplate: isTemplateCreation
          }
        });
        
        showSnackbar('Could not load document. Using sample data.', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadDocument();
  }, [id, templateId, isTemplateCreation]);
  
  // Join document room on load for collaboration
  useEffect(() => {
    if (id && !loading) {
      // Using window.setTimeout to avoid direct access to useCollaboration hook before provider is mounted
      window.setTimeout(() => {
        const event = new CustomEvent('join-document', { detail: { documentId: id } });
        window.document.dispatchEvent(event);
      }, 0);
    }
  }, [id, loading]);
  
  // Handle section content changes with collaboration
  const handleSectionChange = (sectionId: number, content: string) => {
    setDocData({
      ...docData,
      sections: docData.sections.map((section: any) =>
        section.id === sectionId ? { ...section, content } : section
      )
    });
    
    // Notify collaborators of the change
    const event = new CustomEvent('section-content-changed', { 
      detail: { sectionId, content } 
    });
    window.document.dispatchEvent(event);
  };
  
  // Handle text selection with cursor position tracking
  const handleTextSelection = (sectionId: number) => (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const selection = window.getSelection();
    if (selection && selection.toString()) {
      setSelectedText(selection.toString());
    }
    
    // Get cursor position
    const textarea = e.currentTarget;
    const position = textarea.selectionStart;
    
    // Notify collaborators of cursor position
    const event = new CustomEvent('cursor-position-changed', { 
      detail: { sectionId, position } 
    });
    window.document.dispatchEvent(event);
  };
  
  // Handle document save
  const handleSave = async () => {
    setSaving(true);
    
    try {
      const updatedDocument = {
        ...docData,
        metadata: {
          ...docData.metadata,
          updatedAt: new Date().toISOString()
        }
      };
      
      if (isTemplateCreation || isTemplateEdit) {
        // Handle template creation/update
        const templateData = {
          name: docData.title,
          description: docData.content || "Template description",
          content: JSON.stringify(docData.sections.map((section: DocumentSection) => section.content).join('\n\n')),
          category: "Other", // Default category
          sections: docData.sections,
          isPublic: true
        };
        
        if (isTemplateEdit && id) {
          await templateAPI.update(id, templateData);
          showSnackbar('Template updated successfully', 'success');
        } else {
          try {
            const response = await templateAPI.create(templateData);
            if (response.data && response.data.template) {
              navigate(`/templates/edit/${response.data.template._id}`);
              showSnackbar('Template created successfully', 'success');
            } else {
              throw new Error('Invalid response from server');
            }
          } catch (error: any) {
            console.error('Template creation error:', error);
            showSnackbar(`Failed to create template: ${error.response?.data?.message || error.message}`, 'error');
          }
        }
      } else if (id) {
        // Update existing document
        await documentAPI.update(id, updatedDocument);
        showSnackbar('Document saved successfully', 'success');
      } else {
        // Create new document
        const response = await documentAPI.create(updatedDocument);
        navigate(`/documents/${response.data.id}`);
        showSnackbar('Document created successfully', 'success');
      }
    } catch (error: any) {
      console.error('Error saving:', error);
      showSnackbar(`Failed to save: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle document title change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocData({
      ...docData,
      title: e.target.value
    });
  };
  
  // Handle encryption toggle
  const handleToggleEncryption = (encrypt: boolean) => {
    setDocData({
      ...docData,
      isEncrypted: encrypt
    });
    
    showSnackbar(
      encrypt 
        ? 'Document encryption enabled' 
        : 'Document encryption disabled - this document will not be encrypted when saved',
      encrypt ? 'success' : 'info'
    );
  };
  
  // Toggle security settings dialog
  const handleToggleSecuritySettings = () => {
    setShowSecuritySettings(!showSecuritySettings);
  };
  
  // AI feature handlers
  const handleAIFeatureSelect = (feature: string) => {
    setAIFeature(feature);
    setAIResult(null);
    setAIDrawerOpen(true);
  };
  
  const handleApplyAIResult = () => {
    if (!aiResult || !activeSection) return;
    
    handleSectionChange(activeSection, aiResult);
    setAIDrawerOpen(false);
    setAIResult(null);
    showSnackbar('AI suggestion applied successfully', 'success');
  };
  
  const handleRunAIFeature = async () => {
    if (!aiFeature || !activeSection) return;
    
    const sectionContent = docData.sections.find((s: any) => s.id === activeSection)?.content || '';
    
    setAILoading(true);
    
    try {
      let result;
      
      switch (aiFeature) {
        case 'improve':
          result = await aiAPI.improveClause(sectionContent);
          setAIResult(result.data.improvedText);
          break;
        case 'simplify':
          result = await aiAPI.simplifyText(sectionContent);
          setAIResult(result.data.simplifiedText);
          break;
        case 'alternatives':
          result = await aiAPI.suggestAlternatives(sectionContent);
          setAIResult(result.data.alternatives.join('\n\n'));
          break;
        case 'analyze':
          result = await aiAPI.analyzeRisks(sectionContent);
          setRiskAnalysis(result.data.risks);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error using AI feature:', error);
      showSnackbar('Failed to process AI request', 'error');
    } finally {
      setAILoading(false);
    }
  };
  
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Show snackbar notification
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Handle document lock for editing
  const handleLockDocument = () => {
    const event = new CustomEvent('lock-document');
    window.document.dispatchEvent(event);
  };
  
  // Handle document unlock
  const handleUnlockDocument = () => {
    const event = new CustomEvent('unlock-document');
    window.document.dispatchEvent(event);
  };
  
  // Handle document export as PDF
  const handleExportPDF = () => {
    setSaving(true);
    
    try {
      // In a real app, this would call an API endpoint to generate a PDF
      // For demo purposes, we'll simulate a delay and show a success message
      setTimeout(() => {
        const fileName = `${docData.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        showSnackbar(`Document exported as PDF: ${fileName}`, 'success');
        setSaving(false);
        handleMenuClose();
      }, 1500);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showSnackbar('Failed to export document as PDF', 'error');
      setSaving(false);
    }
  };
  
  // Handle document export as DOCX
  const handleExportDOCX = () => {
    setSaving(true);
    
    try {
      // In a real app, this would call an API endpoint to generate a DOCX file
      // For demo purposes, we'll simulate a delay and show a success message
      setTimeout(() => {
        const fileName = `${docData.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
        showSnackbar(`Document exported as DOCX: ${fileName}`, 'success');
        setSaving(false);
        handleMenuClose();
      }, 1500);
    } catch (error) {
      console.error('Error exporting DOCX:', error);
      showSnackbar('Failed to export document as DOCX', 'error');
      setSaving(false);
    }
  };
  
  // Handle showing version history dialog
  const handleShowVersionHistory = () => {
    setShowVersionHistoryDialog(true);
    handleMenuClose();
  };
  
  // Handle showing share dialog
  const handleShowShareDialog = () => {
    // Generate a shareable link
    const baseUrl = window.location.origin;
    const shareableLink = `${baseUrl}/documents/shared/${id || 'new'}?token=${Math.random().toString(36).substring(2, 15)}`;
    setShareLink(shareableLink);
    setShowShareDialog(true);
  };
  
  // Handle copying link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setIsLinkCopied(true);
        setTimeout(() => setIsLinkCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
        showSnackbar('Failed to copy link to clipboard', 'error');
      });
  };
  
  // Handle sharing document via email
  const handleShareViaEmail = () => {
    if (!shareEmail) {
      showSnackbar('Please enter an email address', 'error');
      return;
    }
    
    // In a real app, this would call an API endpoint to send an email
    // For demo purposes, we'll simulate a delay and show a success message
    showSnackbar(`Invitation sent to ${shareEmail}`, 'success');
    setShareEmail('');
  };
  
  // Handle joining a document via CollaborationProvider
  useEffect(() => {
    const handleJoinDocument = (e: Event) => {
      const { documentId } = (e as CustomEvent).detail;
      if (documentId) {
        // Access the useCollaboration hook from inside the component wrapped by the provider
        const collaborationContext = window.document.getElementById('collaboration-context');
        if (collaborationContext) {
          const joinDocument = (collaborationContext as any).joinDocument;
          if (typeof joinDocument === 'function') {
            joinDocument(documentId);
          }
        }
      }
    };
    
    const handleSectionContentChanged = (e: Event) => {
      const { sectionId, content } = (e as CustomEvent).detail;
      if (sectionId && content) {
        const collaborationContext = window.document.getElementById('collaboration-context');
        if (collaborationContext) {
          const updateSectionContent = (collaborationContext as any).updateSectionContent;
          if (typeof updateSectionContent === 'function') {
            updateSectionContent(sectionId, content);
          }
        }
      }
    };
    
    const handleCursorPositionChanged = (e: Event) => {
      const { sectionId, position } = (e as CustomEvent).detail;
      if (sectionId && position !== undefined) {
        const collaborationContext = window.document.getElementById('collaboration-context');
        if (collaborationContext) {
          const updateCursorPosition = (collaborationContext as any).updateCursorPosition;
          if (typeof updateCursorPosition === 'function') {
            updateCursorPosition(sectionId, position);
          }
        }
      }
    };
    
    const handleLockDocument = () => {
      const collaborationContext = window.document.getElementById('collaboration-context');
      if (collaborationContext) {
        const lockDocument = (collaborationContext as any).lockDocument;
        if (typeof lockDocument === 'function') {
          lockDocument().then((result: boolean) => {
            if (result) {
              showSnackbar('Document locked for editing', 'success');
            } else {
              showSnackbar('Could not lock document', 'error');
            }
          });
        }
      }
    };
    
    const handleUnlockDocument = () => {
      const collaborationContext = window.document.getElementById('collaboration-context');
      if (collaborationContext) {
        const unlockDocument = (collaborationContext as any).unlockDocument;
        if (typeof unlockDocument === 'function') {
          unlockDocument().then((result: boolean) => {
            if (result) {
              showSnackbar('Document unlocked', 'success');
            } else {
              showSnackbar('Could not unlock document', 'error');
            }
          });
        }
      }
    };
    
    window.document.addEventListener('join-document', handleJoinDocument);
    window.document.addEventListener('section-content-changed', handleSectionContentChanged);
    window.document.addEventListener('cursor-position-changed', handleCursorPositionChanged);
    window.document.addEventListener('lock-document', handleLockDocument);
    window.document.addEventListener('unlock-document', handleUnlockDocument);
    
    return () => {
      window.document.removeEventListener('join-document', handleJoinDocument);
      window.document.removeEventListener('section-content-changed', handleSectionContentChanged);
      window.document.removeEventListener('cursor-position-changed', handleCursorPositionChanged);
      window.document.removeEventListener('lock-document', handleLockDocument);
      window.document.removeEventListener('unlock-document', handleUnlockDocument);
    };
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <CollaborationProvider>
      <div id="collaboration-context" style={{ display: 'none' }} />
      <Box sx={{ p: 3 }}>
        {/* Document header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <TextField
              variant="standard"
              value={docData.title}
              onChange={handleTitleChange}
              placeholder={isTemplateCreation ? "Enter template name..." : "Enter document title..."}
              fullWidth
              sx={{ 
                '& .MuiInputBase-input': { 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold' 
                } 
              }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!isTemplateCreation && <ActiveCollaborators />}
              
              {!isTemplateCreation && <EncryptionBadge isEncrypted={docData.isEncrypted} showLabel={false} />}
              
              {!isTemplateCreation && (
                <Tooltip title="Security Settings">
                  <IconButton onClick={handleToggleSecuritySettings} sx={{ ml: 1 }}>
                    <SecurityIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              {!isTemplateCreation && (
                <Tooltip title="Collaboration Settings">
                  <IconButton onClick={() => setShowCollaborationDialog(true)} sx={{ ml: 1 }}>
                    <PeopleIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title={isTemplateCreation ? "Save Template" : "Save Document"}>
                <IconButton 
                  onClick={handleSave} 
                  disabled={saving}
                  sx={{ ml: 1 }}
                >
                  {saving ? <CircularProgress size={24} /> : <SaveIcon />}
                </IconButton>
              </Tooltip>
              
              {!isTemplateCreation && (
                <Tooltip title="Share Document">
                  <IconButton onClick={handleShowShareDialog} sx={{ ml: 1 }}>
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title="More Options">
                <IconButton 
                  onClick={handleMenuOpen}
                  sx={{ ml: 1 }}
                >
                  <MoreIcon />
                </IconButton>
              </Tooltip>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                {!isTemplateCreation && (
                  <>
                    <MenuItem onClick={handleExportPDF}>
                      <ListItemIcon>
                        <DownloadIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Export as PDF</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleExportDOCX}>
                      <ListItemIcon>
                        <DownloadIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Export as DOCX</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleShowVersionHistory}>
                      <ListItemIcon>
                        <HistoryIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Version History</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleToggleSecuritySettings}>
                      <ListItemIcon>
                        <SecurityIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Security Settings</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => setShowCollaborationDialog(true)}>
                      <ListItemIcon>
                        <PeopleIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Collaboration Settings</ListItemText>
                    </MenuItem>
                  </>
                )}
                {isTemplateCreation && (
                  <MenuItem onClick={handleMenuClose}>
                    <ListItemIcon>
                      <DescriptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Template Settings</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </Box>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            {isTemplateCreation ? 'Template' : 'Document'} • Last updated: {new Date(docData.metadata.updatedAt).toLocaleString()}
          </Typography>
        </Paper>
        
        {/* Security Settings Dialog */}
        <Dialog 
          open={showSecuritySettings} 
          onClose={handleToggleSecuritySettings}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Security Settings
          </DialogTitle>
          <DialogContent>
            <EncryptionSettings 
              isEncrypted={docData.isEncrypted} 
              onToggleEncryption={handleToggleEncryption}
              disabled={id !== undefined && docData.isEncrypted} // Can't disable encryption on existing encrypted documents
            />
            
            {id !== undefined && docData.isEncrypted && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Encryption cannot be disabled on existing encrypted documents to maintain security.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleToggleSecuritySettings}>Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Document tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Editor" />
            <Tab label="Preview" />
            <Tab label="History" />
          </Tabs>
        </Box>
        
        {/* Editor tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Document sections */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                {docData.sections.map((section: any, index: number) => (
                  <Box key={section.id} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">{section.title}</Typography>
                      <Box>
                        <Tooltip title="Improve with AI">
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => {
                              setActiveSection(section.id);
                              handleAIFeatureSelect('improve');
                            }}
                          >
                            <AIIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Simplify Language">
                          <IconButton 
                            size="small"
                            onClick={() => {
                              setActiveSection(section.id);
                              handleAIFeatureSelect('simplify');
                            }}
                          >
                            <TranslateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Suggest Alternatives">
                          <IconButton 
                            size="small"
                            onClick={() => {
                              setActiveSection(section.id);
                              handleAIFeatureSelect('alternatives');
                            }}
                          >
                            <TipIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <TextField
                      multiline
                      fullWidth
                      minRows={3}
                      value={section.content}
                      onChange={(e) => handleSectionChange(section.id, e.target.value)}
                      onMouseUp={(e) => handleTextSelection(section.id)(e as unknown as React.MouseEvent<HTMLTextAreaElement>)}
                      variant="outlined"
                      placeholder={`Enter ${section.title.toLowerCase()} content...`}
                    />
                  </Box>
                ))}
                
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => {
                    const newSection = {
                      id: Math.max(...docData.sections.map((s: any) => s.id)) + 1,
                      title: 'New Section',
                      content: ''
                    };
                    setDocData({
                      ...docData,
                      sections: [...docData.sections, newSection]
                    });
                  }}
                >
                  Add Section
                </Button>
              </Paper>
            </Grid>
            
            {/* Document metadata and AI insights */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Document Info</Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(docData.metadata.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last modified: {new Date(docData.metadata.updatedAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Author: {docData.metadata.author}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Risk Analysis</Typography>
                
                {riskAnalysis.length > 0 ? (
                  <List dense disablePadding>
                    {riskAnalysis.map((risk, index) => (
                      <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton 
                          dense 
                          onClick={() => {
                            setActiveSection(risk.section);
                            setActiveTab(0);
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <WarningIcon 
                              fontSize="small" 
                              color={
                                risk.severity === 'high' ? 'error' : 
                                risk.severity === 'medium' ? 'warning' : 
                                'info'
                              } 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={risk.description} 
                            secondary={`Section: ${docData.sections.find((s: any) => s.id === risk.section)?.title}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Button
                    startIcon={<WarningIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => handleAIFeatureSelect('analyze')}
                  >
                    Run Risk Analysis
                  </Button>
                )}
              </Paper>
              
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>AI Assistant</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use our AI assistant to improve your document with smart suggestions.
                </Typography>
                
                <List>
                  <ListItemButton onClick={() => handleAIFeatureSelect('improve')}>
                    <ListItemIcon>
                      <AIIcon />
                    </ListItemIcon>
                    <ListItemText primary="Improve Clause" secondary="Enhance clarity and legal protection" />
                  </ListItemButton>
                  
                  <ListItemButton onClick={() => handleAIFeatureSelect('simplify')}>
                    <ListItemIcon>
                      <TranslateIcon />
                    </ListItemIcon>
                    <ListItemText primary="Simplify Language" secondary="Convert legal jargon to plain English" />
                  </ListItemButton>
                  
                  <ListItemButton onClick={() => handleAIFeatureSelect('alternatives')}>
                    <ListItemIcon>
                      <TipIcon />
                    </ListItemIcon>
                    <ListItemText primary="Suggest Alternatives" secondary="Get different versions of the same clause" />
                  </ListItemButton>
                  
                  <ListItemButton onClick={() => handleAIFeatureSelect('analyze')}>
                    <ListItemIcon>
                      <WarningIcon />
                    </ListItemIcon>
                    <ListItemText primary="Analyze Risks" secondary="Identify potential issues in your document" />
                  </ListItemButton>
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* Preview tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom>{docData.title}</Typography>
            
            {docData.sections.map((section: any) => (
              <Box key={section.id} sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>{section.title}</Typography>
                <Typography paragraph>{section.content}</Typography>
              </Box>
            ))}
          </Paper>
        )}
        
        {/* History tab */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Document History</Typography>
            <List>
              {[
                { date: '2023-05-20 14:30', user: 'Current User', action: 'Created document' },
                { date: '2023-05-20 15:45', user: 'Current User', action: 'Updated Scope of Services section' },
                { date: '2023-05-21 09:15', user: 'Current User', action: 'Applied AI improvements to Confidentiality section' }
              ].map((item, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <HistoryIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.action} 
                    secondary={`${item.date} by ${item.user}`} 
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
        
        {/* AI feature drawer */}
        <Drawer
          anchor="right"
          open={aiDrawerOpen}
          onClose={() => setAIDrawerOpen(false)}
          PaperProps={{
            sx: { width: '400px', p: 3 }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {aiFeature === 'improve' && 'Improve Clause'}
              {aiFeature === 'simplify' && 'Simplify Language'}
              {aiFeature === 'alternatives' && 'Alternative Suggestions'}
              {aiFeature === 'analyze' && 'Risk Analysis'}
            </Typography>
            
            {aiFeature !== 'analyze' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Current Text:</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                  <Typography variant="body2">
                    {activeSection && docData.sections.find((s: any) => s.id === activeSection)?.content}
                  </Typography>
                </Paper>
                
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={handleRunAIFeature}
                  disabled={aiLoading}
                >
                  {aiLoading ? <CircularProgress size={24} /> : 'Generate AI Suggestion'}
                </Button>
              </Box>
            )}
            
            {aiFeature === 'analyze' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Our AI will analyze your document for potential legal risks and ambiguities.
                </Typography>
                
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={handleRunAIFeature}
                  disabled={aiLoading}
                >
                  {aiLoading ? <CircularProgress size={24} /> : 'Run Risk Analysis'}
                </Button>
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            {aiLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {aiFeature !== 'analyze' && aiResult && (
                  <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle2" gutterBottom>AI Suggestion:</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50', mb: 2 }}>
                      <Typography variant="body2">{aiResult}</Typography>
                    </Paper>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleApplyAIResult}
                      startIcon={<CheckIcon />}
                    >
                      Apply Suggestion
                    </Button>
                  </Box>
                )}
                
                {aiFeature === 'analyze' && riskAnalysis.length > 0 && (
                  <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle2" gutterBottom>Identified Risks:</Typography>
                    
                    <List>
                      {riskAnalysis.map((risk, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }}>
                          <ListItemIcon>
                            <WarningIcon 
                              color={
                                risk.severity === 'high' ? 'error' : 
                                risk.severity === 'medium' ? 'warning' : 
                                'info'
                              } 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2">{risk.description}</Typography>
                                <Chip 
                                  size="small" 
                                  label={risk.severity} 
                                  color={
                                    risk.severity === 'high' ? 'error' : 
                                    risk.severity === 'medium' ? 'warning' : 
                                    'info'
                                  }
                                  sx={{ ml: 1 }}
                                />
                              </Box>
                            }
                            secondary={`In section: ${docData.sections.find((s: any) => s.id === risk.section)?.title}`}
                          />
                          <Button 
                            size="small" 
                            onClick={() => {
                              setActiveSection(risk.section);
                              setAIDrawerOpen(false);
                              setActiveTab(0);
                            }}
                          >
                            Go to
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </>
            )}
            
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button fullWidth onClick={() => setAIDrawerOpen(false)}>
                Close
              </Button>
            </Box>
          </Box>
        </Drawer>
        
        {/* Collaboration Dialog */}
        <Dialog
          open={showCollaborationDialog}
          onClose={() => setShowCollaborationDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Collaboration Settings
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Active Collaborators
              </Typography>
              <ActiveCollaborators showDetailed />
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Document Editing
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Lock the document to make exclusive changes or unlock to allow others to edit.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  startIcon={<LockIcon />}
                  variant="outlined"
                  onClick={handleLockDocument}
                >
                  Lock for Editing
                </Button>
                
                <Button
                  startIcon={<LockOpenIcon />}
                  variant="outlined"
                  onClick={handleUnlockDocument}
                >
                  Unlock Document
                </Button>
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Invite Collaborators
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Share this document with other users for collaboration.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Email Address"
                  placeholder="example@email.com"
                  variant="outlined"
                  fullWidth
                  size="small"
                />
                
                <Button variant="contained">
                  Invite
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCollaborationDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Share Document Dialog */}
        <Dialog
          open={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Share Document
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Share via Link
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <TextField
                  value={shareLink}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<FileCopyIcon />}
                  onClick={handleCopyLink}
                >
                  {isLinkCopied ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Anyone with this link can view this document
              </Typography>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Share via Email
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  label="Email Address"
                  placeholder="example@email.com"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  onClick={handleShareViaEmail}
                >
                  Send
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowShareDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Version History Dialog */}
        <Dialog
          open={showVersionHistoryDialog}
          onClose={() => setShowVersionHistoryDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Version History
          </DialogTitle>
          <DialogContent>
            <List>
              {[
                { 
                  version: 'v1.3', 
                  date: '2023-05-21 09:15', 
                  user: 'Current User', 
                  changes: 'Applied AI improvements to Confidentiality section',
                  current: true
                },
                { 
                  version: 'v1.2', 
                  date: '2023-05-20 15:45', 
                  user: 'Current User', 
                  changes: 'Updated Scope of Services section',
                  current: false
                },
                { 
                  version: 'v1.1', 
                  date: '2023-05-20 14:30', 
                  user: 'Current User', 
                  changes: 'Created document',
                  current: false
                }
              ].map((item, index) => (
                <ListItem 
                  key={index} 
                  sx={{ 
                    py: 2, 
                    bgcolor: item.current ? 'primary.50' : 'transparent',
                    borderRadius: 1
                  }}
                >
                  <ListItemIcon>
                    <HistoryIcon color={item.current ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{item.version}</Typography>
                        {item.current && (
                          <Chip 
                            label="Current" 
                            color="primary" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2">{item.changes}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.date} by {item.user}
                        </Typography>
                      </>
                    }
                  />
                  {!item.current && (
                    <Button 
                      size="small" 
                      variant="outlined"
                      sx={{ ml: 2 }}
                    >
                      Restore
                    </Button>
                  )}
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowVersionHistoryDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </CollaborationProvider>
  );
};

export default DocumentEditorPage;